import { useUser } from '@clerk/nextjs';
import { datadogLogs } from '@datadog/browser-logs';
import { type IdentifyTraits } from '@rudderstack/analytics-js';
import {
  OrganizationContext,
  OrganizationContextProps,
} from '@web-app/hocs/organization';
import { RUDDERSTACK_ANALYTICS_ERROR_LOG_PREFIX } from '@web-app/utils/api/logger-prefixes';
import { getBrowser } from '@web-app/utils/common.utils';
import { isDevelopment } from '@web-app/utils/env.utils';
import { rudderstackAnalytics } from '@web-app/utils/rudderstackAnalytics';
import { usePathname } from 'next/navigation';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';

export enum EVENT_NAMES {
  SIGN_IN = 'sign_in',
  JOIN_SESSION = 'join_session',
  PROVIDER_JOIN_SESSION = 'provider_join_session',
  VIEW_TASKS = 'view_tasks',
  VIEW_MANAGE_PRACTICE = 'view_manage_practice',
  CLICK_TO_REVIEW_TASK = 'click_to_review_task',
  VIEW_CHARTING_NOTE = 'views_charting_note',
  CHARTING_NOTE_BLANK = 'charting_note_blank',
  EDITS_CHARTING_NOTE = 'edits_charting_note',
  SAVES_CHARTING_NOTE = 'saves_charting_note',
  CLICK_REGENERATE_ICON = 'click_regenerate_icon',
  CLICK_CANCEL_REGENERATION = 'click_cancel_regeneration',
  REGENERATES_CHARTING_NOTE = 'regenerates_charting_note',
  REGENERATES_SESSION_DETAILS = 'regenerates_session_details',
  SUBMITS_CHARTING_NOTE = 'submits_charting_note',
  CLICK_HEALTHIE_PROFILE_LINK = 'clicks_healthie_profile_link',
  VIEWS_FOLLOW_UP_MESSAGE = 'views_follow_up_message',
  CLICK_REGENERATE_FOLLOW_UP_ICON = 'click_regenerate_follow_up_icon',
  CLICK_CANCEL_FOLLOW_UP_REGENERATION = 'click_cancel_follow_up_regeneration',
  REGENERATES_FOLLOW_UP = 'regenerates_follow_up',
  EDITS_FOLLOW_UP = 'edits_follow_up',
  EDITS_CARE_PLAN = 'edits_care_plan',
  FOLLOW_UP_BLANK = 'follow_up_blank',
  ATTACHMENT_ADDED = 'attachment_added',
  SENDS_FOLLOW_UP_MESSAGES = 'sends_follow_up_message',
  SENDS_FOLLOW_UP_AND_CARE_PLAN_MESSAGES = 'sends_follow_up_and_care_plan_message',
  CLICKS_DO_LATER = 'clicks_do_later',
  CLICKS_HEALTHIE_PROFILE_LINK_FOLLOW_UP = 'clicks_healthie_profile_link_follow_up',
  CLICKS_REVIEWED_TASK = 'clicks_reviewed_task',
  CLICKS_DELETE_TASK_ICON = 'clicks_delete_task_icon',
  UPDATES_DELETE_REASON = 'updates_delete_reason',
  CLICKS_DELETE_TASK_BUTTON = 'clicks_delete_task_button',
  CLICKS_CANCEL_BUTTON = 'clicks_cancel_button',
  LOGOUT = 'logout',
  VIEW_MEAL_PLAN_INSTRUCTIONS = 'view_meal_plan_instructions',
  EDITS_MEAL_PLAN_INSTRUCTIONS = 'edits_meal_plan_instructions',
  CLICKS_SKIP_MEAL_PLAN_INSTRUCTION_PAGE = 'clicks_skip_meal_plan_instruction_page',
  CLICKS_GENERATE_PLAN = 'clicks_generate_plan',
  VIEW_MEAL_PLAN_OUTLINE = 'view_meal_plan_outline',
  EDITS_MEAL_PLAN_OUTLINE = 'edits_meal_plan_outline',
  CLICKS_SKIP_MEAL_PLAN_OUTLINE_PAGE = 'clicks_skip_meal_plan_outline_page',
  CLICKS_REGENERATE_OUTLINE = 'clicks_regenerate_outline',
  CLICKS_GENERATE_RECIPES = 'clicks_generate_recipes',
  VIEW_FULL_MEAL_PLAN_RECIPES = 'view_full_meal_plan_recipes',
  CLICKS_TO_VIEW_SPECIFIC_RECIPE = 'clicks_to_view_specific_recipe',
  EDITS_MEAL_RECIPE = 'edits_meal_recipe',
  CLICKS_REGENERATE_MEAL_PLAN = 'clicks_regenerate_meal_plan',
  CLICKS_SKIP_REVIEW_MEAL_PLAN_PAGE = 'clicks_skip_review_meal_plan_page',
  CLICKS_SEND_MEAL_PLAN = 'clicks_send_meal_plan',
  VIEW_FOLLOW_UP_PATIENT_LIST = 'view_follow_up_patient_list',
  CLICK_PATIENT_HEALTHIE_LINK = 'click_patient_healthie_link',
  COPY_PATIENT_PHONE_NUMBER = 'copy_patient_phone_number',
  CLICK_TEMPLATE_LINK = 'click_template_link',
  CHARTING_NOTE_TEMPLATE_SELECTED = 'charting_note_template_selected',
  CLICK_SCHEDULE_EMAIL_LINK = 'click_schedule_email_link',
  CLICK_COPY_BOOKING_LINK = 'click_copy_booking_link',
  CLICK_HEALTHIE_ARCHIVE_LINK = 'click_healthie_archive_link',
  VIEW_PATIENTS_PAGE = 'view_patients_page',
  VIEW_BENEFIT_CHECK_REQUEST_PAGE = 'view_benefit_check_request_page',
  TOGGLE_PATIENTS_FILTER = 'toggle_patients_filter',
  CLICK_PATIENT_PROFILE_LINK = 'click_patient_profile_link',
  LOAD_MORE_PATIENTS = 'load_more_patients',
  CLICK_GENERATE_LINK = 'click_generate_link',
  CLICK_END_SESSION_BUTTON = 'click_end_session_button',
  VIEW_PATIENT_PROFILE_PAGE = 'view_patient_profile_page',
  CLICKS_LEAVE_CALL_REDIRECT = 'clicks_leave_call_redirect',
  CLICKS_RETURN_TO_CALL = 'clicks_return_to_call',
  CLICKS_PATIENT_BENEFITS = 'clicks_patient_benefits',
  CLICKS_PATIENT_INFO = 'clicks_patient_info',
  CLICKS_INSURANCE = 'clicks_insurance',
  // Macro dashboard analytics
  SWITCH_MACRO_TAB = 'switch_macro_tab',
  SAVE_DAILY_GOALS = 'save_daily_goals',
  VIEW_DAILY_GOALS = 'view_daily_goals',
  EDIT_DAILY_GOALS = 'edit_daily_goals',
  // Call quality survey analytics
  VIEW_CALL_QUALITY_SURVEY = 'view_call_quality_survey',
  SUBMIT_CALL_QUALITY_SURVEY = 'submit_call_quality_survey',
  SUBMIT_PROVIDER_EXPERIENCE_SURVEY = 'submit_provider_experience_survey',
  SKIP_CALL_QUALITY_SURVEY = 'skip_call_quality_survey',
  // Video call lifecycle analytics
  PATIENT_REQUESTED_TO_JOIN = 'patient_requested_to_join',
  PROVIDER_ADMITTED_PATIENT = 'provider_admitted_patient',
  PARTICIPANT_JOINED_CALL = 'participant_joined_call',
  PARTICIPANT_LEFT_CALL = 'participant_left_call',
  // Session prep tab analytics
  SESSION_RECAP_CITATION_CLICKED = 'session_recap_citation_clicked',
  CROSS_SELL_BOOKING_OFFER_VIEWED = 'cross_sell_booking_offer_viewed',
  CROSS_SELL_BOOKING_LINK_COPIED = 'cross_sell_booking_link_copied',
  CROSS_SELL_BOOKING_LINK_SENT = 'cross_sell_booking_link_sent',
  // Session goals analytics
  GOALS_WIDGET_VIEWED = 'goals_widget_viewed',
  GOAL_ADDED = 'goal_added',
  GOAL_ARCHIVED = 'goal_archived',
  GOAL_EDITED = 'goal_edited',
  // Dial out analytics
  DIAL_OUT_INITIATED = 'dial_out_initiated',
  DIAL_OUT_HANGUP_CLICKED = 'dial_out_hangup_clicked',
  // Lab documents analytics
  LABS_TAB_VIEWED = 'labs_tab_viewed',
  LAB_DOCUMENT_DRAWER_OPENED = 'lab_document_drawer_opened',
  LAB_DOCUMENT_DRAWER_CLOSED = 'lab_document_drawer_closed',
  LAB_DOCUMENT_DOWNLOAD_BUTTON_CLICKED = 'lab_document_download_button_clicked',
  LAB_DOCUMENT_OPENED_IN_NEW_TAB = 'lab_document_opened_in_new_tab',
  // Walmart shopping insights analytics
  WALMART_INSIGHTS_BANNER_VIEWED = 'walmart_insights_banner_viewed',
  WALMART_INSIGHTS_DRAWER_OPENED = 'walmart_insights_drawer_opened',
  WALMART_INSIGHTS_DRAWER_CLOSED = 'walmart_insights_drawer_closed',
  WALMART_INSIGHTS_DOWNLOAD_CLICKED = 'walmart_insights_download_clicked',
  WALMART_INSIGHTS_OPENED_IN_NEW_TAB = 'walmart_insights_opened_in_new_tab',
  // Medications tab analytics
  MEDICATIONS_TAB_VIEWED = 'medications_tab_viewed',
  // In-session scheduling analytics
  SCHEDULING_DRAWER_OPENED = 'scheduling_drawer_opened',
  SCHEDULING_APPOINTMENT_BOOKED = 'scheduling_appointment_booked',
  // Calendar analytics
  CALENDAR_ACTION = 'calendar_action',
  // Chat analytics
  VIEW_CHAT_CHANNEL = 'view_chat_channel',
  GLUCOSE_TAB_VIEWED = 'glucose_tab_viewed',
  // Provider attestation documents analytics
  PROVIDER_DOCUMENT_OPENED = 'provider_document_opened',
  PROVIDER_DOCUMENT_ATTESTATION_STARTED =
    'provider_document_attestation_started',
  PROVIDER_DOCUMENT_ATTESTATION_COMPLETED =
    'provider_document_attestation_completed',
  PROVIDER_DOCUMENT_ATTESTATION_FAILED =
    'provider_document_attestation_failed',
}

interface AnalyticsContextType {
  /**
   * Track an analytics event
   * @param eventName - Event identifier (preferably from EVENT_NAMES enum)
   * @param eventProperties - Optional event data (user context added automatically)
   */
  trackEvent: (
    eventName: string,
    eventProperties?: Record<string, any>,
  ) => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(
  undefined,
);

// TODO: Consider making userId not optional and use the Clerk user ID by always.
// This prevents drift in Rudderstack identity graph and ensures non-collision with patients as currently
// they are sharing Production workspace.
/**
 * Analytics Provider Component
 *
 * Handles RudderStack user identification, session management, and event
 * tracking.
 *
 * Features:
 * - RudderStack: Event tracking and user identification with comprehensive profile data
 * - Automatic context: user ID, org ID, device info, browser, pathname
 *
 * @param children - React children components
 * @param userId - Optional override for user ID (defaults to Clerk user ID)
 */
export const AnalyticsProvider: FC<{
  children: ReactNode;
  userId?: string;
}> = ({ children, userId }) => {
  const { user } = useUser();
  const pathname = usePathname();
  const { organizationId } =
    useContext<OrganizationContextProps>(OrganizationContext);
  // RudderStack user identification - memoize identify data
  const identifyData = useMemo((): IdentifyTraits | null => {
    const userIdToUse = userId || user?.id;
    if (!userIdToUse) return null;

    return {
      // Basic user fields
      email: user?.primaryEmailAddress?.emailAddress,
      firstName: user?.firstName || undefined,
      lastName: user?.lastName || undefined,
      fullName: user?.fullName || undefined,

      // Timestamps
      createdAt: user?.createdAt
        ? new Date(user.createdAt).toISOString()
        : undefined,
      lastSignInAt: user?.lastSignInAt
        ? new Date(user.lastSignInAt).toISOString()
        : undefined,

      // Business context
      healthieUserId: user?.publicMetadata?.healthie_user_id as number,
      orgId: organizationId,
    };
  }, [
    userId,
    user?.id,
    user?.primaryEmailAddress?.emailAddress,
    user?.firstName,
    user?.lastName,
    user?.fullName,
    user?.createdAt,
    user?.lastSignInAt,
    user?.publicMetadata?.healthie_user_id,
    organizationId,
  ]);

  useEffect(() => {
    if (!identifyData) return;

    const userIdToUse = userId || user?.id;
    if (!userIdToUse) return;

    rudderstackAnalytics.identify(userIdToUse, identifyData);
  }, [identifyData, userId, user?.id]);

  // Helper function to prepare base event properties
  const prepareBaseEventProperties = useCallback(() => {
    const userIdToUse = userId || user?.id;

    const baseProperties: Record<string, any> = {
      healthieId: user?.publicMetadata?.healthie_user_id as number,
      orgId: organizationId,
      provider: !!userIdToUse, // Use userIdToUse instead of user to avoid dependency
      userId: userIdToUse,
      page: pathname,
    };

    // Safe browser detection (SSR-safe)
    if (typeof window !== 'undefined') {
      baseProperties.deviceType =
        (window.navigator as any)?.userAgentData?.platform ||
        window.navigator?.platform;
      baseProperties.browserType = getBrowser();
    }

    // Add user context from Clerk
    if (user?.primaryEmailAddress?.emailAddress) {
      baseProperties.email = user.primaryEmailAddress.emailAddress;
    }

    return baseProperties;
  }, [
    userId,
    user?.id,
    user?.primaryEmailAddress?.emailAddress,
    user?.publicMetadata?.healthie_user_id,
    organizationId,
    pathname,
  ]);

  // Helper function to handle RudderStack tracking with error handling
  const trackRudderstackEvent = useCallback(
    (eventName: string, eventProperties: Record<string, any>) => {
      try {
        rudderstackAnalytics.track(eventName, eventProperties);
      } catch (error) {
        datadogLogs.logger.error(
          `${RUDDERSTACK_ANALYTICS_ERROR_LOG_PREFIX} RudderStack tracking failed`,
          {
            eventName,
            eventProperties,
            error:
              error instanceof Error
                ? { message: error.message, stack: error.stack }
                : String(error),
            timestamp: new Date().toISOString(),
          },
        );

        // Also log to console in development for debugging
        if (isDevelopment) {
          // oxlint-disable-next-line no-console
          console.warn(
            'RudderStack tracking failed:',
            error instanceof Error
              ? { message: error.message, stack: error.stack }
              : String(error),
          );
        }
      }
    },
    [],
  );

  const trackEvent = useCallback(
    (eventName: string, eventProperties?: Record<string, any>) => {
      // Only track valid event names
      if (!Object.values(EVENT_NAMES).includes(eventName as EVENT_NAMES)) {
        return;
      }

      // Prepare event properties
      const baseProperties = prepareBaseEventProperties();

      const rudderstackProperties = {
        ...baseProperties,
        ...eventProperties,
        timestamp: new Date().toISOString(), // Always use ISO for RudderStack
      };

      trackRudderstackEvent(eventName, rudderstackProperties);
    },
    [prepareBaseEventProperties, trackRudderstackEvent],
  );

  return (
    <AnalyticsContext.Provider
      value={{
        trackEvent,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};

/**
 * Hook to access analytics functionality
 *
 * Must be used within an AnalyticsProvider component.
 * Returns trackEvent function for sending events to RudderStack.
 *
 * @returns {AnalyticsContextType} Analytics context with trackEvent function
 * @throws {Error} When used outside of AnalyticsProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { trackEvent } = useAnalytics();
 *
 *   const handleClick = () => {
 *     trackEvent(EVENT_NAMES.CLICK_BUTTON, {
 *       buttonName: 'save',
 *       formStep: 2
 *     });
 *   };
 *
 *   return <button onClick={handleClick}>Save</button>;
 * }
 * ```
 */
export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
